import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { authHeader } from '../../services/authHeader';
import styles from './Scoreboard.module.css';
import useGameSocket from '../../hooks/useGameSocket';

/** Home = player1/2, away = player3/4 (matches backend _finish_game ordering). `name` is username. */
const usernamesFromGameOverPlayers = (p) => {
    if (!p) return { home: [], away: [] };
    return {
        home: [p.player1?.name, p.player2?.name].filter(Boolean),
        away: [p.player3?.name, p.player4?.name].filter(Boolean),
    };
};

const Scoreboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { gameID: routeGameID } = useParams();
    const user = useSelector((state) => state.user);
    const authUsername = useSelector((state) => state.auth.username);
    /** Login sets auth.username immediately; profile may load into user later — same pattern as Lobby. */
    const currentUsername = user?.username || authUsername || null;

    const locationState = location.state || {};
    const [gameID] = useState(locationState.gameID || routeGameID);

    const [team1Player1, setTeam1Player1] = useState(locationState.team1Player1 || '');
    const [team1Player2, setTeam1Player2] = useState(locationState.team1Player2 || '');
    const [team2Player1, setTeam2Player1] = useState(locationState.team2Player1 || '');
    const [team2Player2, setTeam2Player2] = useState(locationState.team2Player2 || '');

    const [score1, setScore1] = useState(0);
    const [score2, setScore2] = useState(0);
    const [panelPlayer, setPanelPlayer] = useState(null);
    const [panelSize, setPanelSize] = useState('full');
    const [cupPosition, setCupPosition] = useState(null);
    const [winnerMessage, setWinnerMessage] = useState('');
    const [winnerColor, setWinnerColor] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [players, setPlayers] = useState(null);
    const [saves, setSaves] = useState({});
    const [flashActive, setFlashActive] = useState(false);
    const [sinkMessage, setSinkMessage] = useState('');
    const [isLap, setIsLap] = useState(false);
    const [loading, setLoading] = useState(true);
    const [lastAction, setLastAction] = useState(null);
    const [undoStep, setUndoStep] = useState(0); // 0=hidden, 1=first confirm, 2=final confirm
    const [quitStep, setQuitStep] = useState(0); // 0=hidden, 1=first confirm, 2=final confirm
    const [wsConnected, setWsConnected] = useState(true);
    const [lobbyTerminated, setLobbyTerminated] = useState(false);
    const [gameAbandoned, setGameAbandoned] = useState(false);
    const [abandonReason, setAbandonReason] = useState(null);
    const [abandonedBy, setAbandonedBy] = useState(null);

    const buildCupValues = (cups) =>
        cups.reduce((acc, { value, position, team }) => {
            if (!acc[team]) acc[team] = {};
            acc[team][position] = parseFloat(value);
            return acc;
        }, {});

    const buildCupVersions = (cups) =>
        cups.reduce((acc, { version, position, team }) => {
            if (!acc[team]) acc[team] = {};
            acc[team][position] = version;
            return acc;
        }, {});

    const [cupVersions, setCupVersions] = useState({});

    const [currentCupValues, setCurrentCupValues] = useState(() => {
        if (locationState.cupValues) {
            return buildCupValues(locationState.cupValues);
        }
        return {};
    });

    const fetchGameState = () => {
        if (!gameID) return;
        axios.get(`${process.env.REACT_APP_API_URL}/api/g/gamestate/${gameID}/`)
            .then(({ data }) => {
                if (data.completed) {
                    navigate('/main');
                    return;
                }
                if (data.abandoned) {
                    setAbandonReason(data.abandon_reason || null);
                    setAbandonedBy(data.abandoned_by || null);
                    setGameAbandoned(true);
                    return;
                }
                const p1 = data.players.home[0] || {};
                const p2 = data.players.home[1] || {};
                const p3 = data.players.away[0] || {};
                const p4 = data.players.away[1] || {};

                setTeam1Player1(p1.name || '');
                setTeam1Player2(p2.name || '');
                setTeam2Player1(p3.name || '');
                setTeam2Player2(p4.name || '');
                setScore1(parseFloat(data.score1));
                setScore2(parseFloat(data.score2));
                setCurrentCupValues(buildCupValues(data.cups));
                setCupVersions(buildCupVersions(data.cups));
            })
            .catch((err) => {
                console.error('Failed to load game state:', err);
                if (err.response?.status === 404) navigate('/main');
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchGameState();
    }, [gameID]); // eslint-disable-line react-hooks/exhaustive-deps

    const [userRecord, setUserRecord] = useState(null);
    const [recordDisplay, setRecordDisplay] = useState(null);
    const statsFetchedForGameRef = React.useRef(null);

    useEffect(() => {
        if (!winnerMessage || !winnerColor || !currentUsername || !players) return;
        const { home, away } = usernamesFromGameOverPlayers(players);
        if (home.length === 0 && away.length === 0) return;
        const dedupeKey = `${gameID}|${currentUsername}`;
        if (statsFetchedForGameRef.current === dedupeKey) return;
        statsFetchedForGameRef.current = dedupeKey;
        const userOnTeam1 = home.includes(currentUsername);
        const userOnTeam2 = away.includes(currentUsername);
        const won = (userOnTeam1 && winnerColor === 'home') || (userOnTeam2 && winnerColor === 'away');
        axios.get(`${process.env.REACT_APP_API_URL}/api/c/getstats/?username=${currentUsername}`, authHeader())
            .then(res => {
                const stats = res.data.player_stats;
                const wins = stats?.wins ?? (won ? 1 : 0);
                const losses = stats?.losses ?? (won ? 0 : 1);
                setUserRecord({ wins, losses, won });
                setRecordDisplay(won ? wins - 1 : losses - 1);
            })
            .catch(() => {
                setUserRecord({ wins: won ? 1 : 0, losses: won ? 0 : 1, won });
                setRecordDisplay(0);
            });
    }, [winnerMessage, winnerColor, currentUsername, players, gameID]);

    useEffect(() => {
        if (recordDisplay === null || !userRecord) return;
        const target = userRecord.won ? userRecord.wins : userRecord.losses;
        if (recordDisplay >= target) return;
        const t = setTimeout(() => setRecordDisplay(n => n + 1), 1800);
        return () => clearTimeout(t);
    }, [recordDisplay, userRecord]);

    const { closeSocket } = useGameSocket(gameID, (data) => {
        if (data.type === 'cup_hit') {
            setScore1(parseFloat(data.score1));
            setScore2(parseFloat(data.score2));
            setCurrentCupValues(prev => ({
                ...prev,
                [data.team]: {
                    ...prev[data.team],
                    [data.position]: parseFloat(data.cup_value),
                },
            }));
            if (data.version !== undefined) {
                setCupVersions(prev => ({
                    ...prev,
                    [data.team]: { ...prev[data.team], [data.position]: data.version },
                }));
            }
            setLastAction(data.last_action || null);
        } else if (data.type === 'cup_revert') {
            setScore1(parseFloat(data.score1));
            setScore2(parseFloat(data.score2));
            setCurrentCupValues(prev => ({
                ...prev,
                [data.team]: {
                    ...prev[data.team],
                    [data.position]: parseFloat(data.cup_value),
                },
            }));
            if (data.version !== undefined) {
                setCupVersions(prev => ({
                    ...prev,
                    [data.team]: { ...prev[data.team], [data.position]: data.version },
                }));
            }
            setLastAction(data.last_action || null);
            setUndoStep(0);
            if (data.reverted_by) {
                triggerFlash();
                setSinkMessage(`${data.reverted_by} reverted play`);
                setTimeout(() => setSinkMessage(''), 1200);
            }
        } else if (data.type === 'game_abandoned') {
            setAbandonReason(data.reason || null);
            setAbandonedBy(data.abandoned_by || null);
            setGameAbandoned(true);
        } else if (data.type === 'saves_update') {
            setSaves(prev => ({ ...prev, [data.player]: data.saves }));
        } else if (data.type === 'game_over') {
            const s1 = parseFloat(data.score1);
            const s2 = parseFloat(data.score2);
            const hp = data.players || {};
            setScore1(s1);
            setScore2(s2);
            const home1 = hp.player1?.name || '';
            const home2 = hp.player2?.name || '';
            const away1 = hp.player3?.name || '';
            const away2 = hp.player4?.name || '';
            if (data.winning_team === 0) {
                setWinnerMessage(`Players ${home1} and ${home2} have won with the score ${s1} to ${s2}. Click end game to finish.`);
                setWinnerColor('home');
            } else {
                setWinnerMessage(`Players ${away1} and ${away2} have won with the score ${s2} to ${s1}. Click end game to finish.`);
                setWinnerColor('away');
            }
            setPlayers(data.players);
            setIsLap(data.lap || false);
        }
    }, setWsConnected, fetchGameState, () => setLobbyTerminated(true));

    const triggerFlash = () => {
        setFlashActive(true);
        setTimeout(() => setFlashActive(false), 300);
    };

    const updateDB = async (player, gameID, team, position, value, hitMessage, cupVersion, isOwnCup = false) => {
        const payload = {
            player: player,
            value: value,
            gameID: gameID,
            team: team,
            position: position,
            cupVersion: cupVersion,
        };

        try {
            const response = await axios.patch(`${process.env.REACT_APP_API_URL}/api/g/updategamecup/`, payload, authHeader());
            const { status: responseStatus, score1: new_score1, score2: new_score2, cup_value: new_cup_value, version: new_version } = response.data;

            if (responseStatus === 'stale' || responseStatus === 'already_taken') {
                setCurrentCupValues(prev => ({
                    ...prev,
                    [team]: { ...prev[team], [position]: parseFloat(new_cup_value) },
                }));
                return;
            }

            if (responseStatus === 'game_over') {
                setScore1(parseFloat(new_score1));
                setScore2(parseFloat(new_score2));
                if (new_version !== undefined) {
                    setCupVersions(prev => ({
                        ...prev,
                        [team]: { ...prev[team], [position]: new_version },
                    }));
                }
                setCurrentCupValues(prevValues => {
                    const updatedValues = { ...prevValues };
                    updatedValues[team] = { ...updatedValues[team], [position]: parseFloat(new_cup_value) };
                    return updatedValues;
                });
                setLastAction(null);
                setUndoStep(0);
                return;
            }

            setScore1(new_score1);
            setScore2(new_score2);

            if (new_version !== undefined) {
                setCupVersions(prev => ({
                    ...prev,
                    [team]: { ...prev[team], [position]: new_version },
                }));
            }

            setLastAction({
                player,
                action_type: isOwnCup ? 'OWN' : value === 1 ? 'SINK' : 'HIT',
            });
            setUndoStep(0);

            triggerFlash();
            setSinkMessage(hitMessage || 'SINK!');
            setTimeout(() => setSinkMessage(''), 800);

            setCurrentCupValues(prevValues => {
                const updatedValues = { ...prevValues };
                updatedValues[team][position] = parseFloat(new_cup_value);
                return updatedValues;
            });
        } catch (error) {
            console.error("Error updating the score:", error);
            showError("Error updating the score. Please try again.");
        }
    };

    const handleSaveAdjust = (player, change) => {
        setSaves(prevSaves => {
            const updated = Math.max(0, (prevSaves[player] ?? 0) + change);
            axios.patch(`${process.env.REACT_APP_API_URL}/api/g/updatesaves/`, { gameID, player, saves: updated }, authHeader())
                .catch((err) => console.error('Error syncing saves:', err));
            return { ...prevSaves, [player]: updated };
        });
    };

    const showError = (msg) => {
        setErrorMessage(msg);
        setTimeout(() => setErrorMessage(''), 3000);
    };

    const closePanel = () => {
        setCupPosition(null);
        setPanelPlayer(null);
        setPanelSize('full');
    };

    const revertDB = async () => {
        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/api/g/revertgamecup/`,
                { gameID, reverter: currentUsername },
                authHeader()
            );
            const { status: responseStatus } = response.data;

            if (responseStatus === 'no_history') {
                setLastAction(null);
                setUndoStep(0);
                showError('Nothing left to undo.');
            }
        } catch (error) {
            console.error('Error reverting cup:', error);
            showError('Error reverting cup. Please try again.');
        }
    };

    const handleCupClick = (cup) => {
        setErrorMessage('');
        setPanelPlayer(null);
        const cupValue = currentCupValues[cup.team]?.[cup.position];
        setPanelSize(cupValue === 0.5 ? 'half' : 'full');
        setCupPosition(cup);
    };

    const handleConfirm = () => {
        if (!panelPlayer || !panelSize) return;
        const value = panelSize === 'half' ? 0.5 : 1;
        if (cupPosition) {
            const cupValue = currentCupValues[cupPosition.team]?.[cupPosition.position] || 0;
            if (cupValue === 0) {
                showError("The cup has already been taken!");
            } else if (value === 1 && cupValue === 0.5) {
                showError("A full cup cannot be taken after a half cup!");
            } else {
                const isOwnCupHit = boolCheckOwnCup(panelPlayer, cupPosition);
                const hitMessage = isOwnCupHit ? 'SELF-DAMAGE' : value === 0.5 ? 'HIT!' : 'SINK!';
                const cupVersion = cupVersions[cupPosition.team]?.[cupPosition.position];
                updateDB(panelPlayer, gameID, cupPosition.team, cupPosition.position, value, hitMessage, cupVersion, isOwnCupHit);
            }
        }
        closePanel();
    };

    const boolCheckOwnCup = (player, cup) => {
        return (
            ((player === team1Player1 || player === team1Player2) && cup.team === "Home") ||
            ((player === team2Player1 || player === team2Player2) && cup.team === "Away")
        );
    };

    const positionNames = {
        RT: "Right",
        LF: "Left",
        CT: "Center",
        BK: "Back",
        FR: "Front",
    };

    const renderCup = (team, position) => {
        const value = currentCupValues[team]?.[position] ?? 0;
        const isGone = value === 0;
        const isHalf = value === 0.5;
        const teamClass = team === 'Home' ? styles.cupHome : styles.cupAway;
        const stateClass = isGone ? styles.cupGone : isHalf ? styles.cupHalf : '';
        return (
            <div
                key={`${team}-${position}`}
                className={`${styles.cup} ${teamClass} ${stateClass}`}
                onClick={() => !isGone && handleCupClick({ team, position })}
            >
                {!isGone && <span className={styles.cupValue}>{value}</span>}
            </div>
        );
    };

    // Victory screen — team membership from WebSocket payload (same source as winning_team / lap)
    if (winnerMessage && players) {
        const { home: homeWinNames, away: awayWinNames } = usernamesFromGameOverPlayers(players);
        const userOnTeam1 = homeWinNames.includes(currentUsername);
        const userOnTeam2 = awayWinNames.includes(currentUsername);
        const userOnWinningTeam = (userOnTeam1 && winnerColor === 'home') || (userOnTeam2 && winnerColor === 'away');
        const userOnLosingTeam = (userOnTeam1 && winnerColor === 'away') || (userOnTeam2 && winnerColor === 'home');
        const isLapLoss = isLap && userOnLosingTeam;
        const isLapWin = isLap && userOnWinningTeam;
        const isLoss = !isLap && userOnLosingTeam;

        return (
            <div className={styles.victoryScreen}>
                <div className={styles.scanlines} />
                <div className={`${styles.victoryFlood} ${isLapLoss ? styles.lapLossFlood : isLoss ? styles.victoryFloodLoss : isLapWin ? styles.lapFlood : styles.victoryFloodWin}`} />
                <div className={styles.victoryContent}>
                    <div className={`${styles.winnerLabel} ${isLapLoss ? styles.lapLossLabel : isLoss ? styles.lossLabelRed : isLapWin ? styles.lapWinnerLabel : ''}`}>
                        {isLapLoss ? 'LAPPED' : isLoss ? 'LOSER' : isLapWin ? "LAPPED 'EM" : 'WINNER'}
                    </div>
                    {isLapWin && <div className={styles.lapSublabel}>Perfect Game</div>}
                    {isLapLoss && <div className={styles.lapLossSublabel}>All Cups on Table</div>}
                    {userRecord && (
                        <div className={styles.recordSection}>
                            <div className={styles.recordLabel}>All-time record</div>
                            <div className={styles.recordLine}>
                                <span className={`${styles.recordStat} ${userRecord.won ? styles.recordActive : styles.recordDim}`}>
                                    <span key={userRecord.won ? recordDisplay : userRecord.wins} className={styles.recordTick}>
                                        {userRecord.won ? recordDisplay : userRecord.wins}
                                    </span>W
                                </span>
                                <span className={styles.recordSep}>·</span>
                                <span className={`${styles.recordStat} ${!userRecord.won ? styles.recordActive : styles.recordDim}`}>
                                    <span key={!userRecord.won ? recordDisplay : userRecord.losses} className={styles.recordTick}>
                                        {!userRecord.won ? recordDisplay : userRecord.losses}
                                    </span>L
                                </span>
                            </div>
                        </div>
                    )}
                    <div className={styles.statsGrid}>
                        {[players.player1, players.player2, players.player3, players.player4].map((player, index) => (
                            <div key={index} className={`${styles.statCard} ${index < 2 ? styles.statCardHome : styles.statCardAway}`}>
                                <div className={styles.statName}>{player?.name}</div>
                                <div className={styles.statRow}><span>Half Cups</span><span>{player?.halfCups}</span></div>
                                <div className={styles.statRow}><span>Full Cups</span><span>{player?.fullCups}</span></div>
                                <div className={styles.statRow}><span>Own Cups</span><span>{player?.ownCups}</span></div>
                                <div className={styles.statRow}><span>Saves</span><span>{player?.saves}</span></div>
                            </div>
                        ))}
                    </div>
                    <div className={styles.endButtons}>
                        <button className={styles.endGameBtn} onClick={() => navigate('/main')}>BACK TO HOME</button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) return null;

    // Active game screen
    return (
        <div className={styles.gameContainer}>
            {!wsConnected && !lobbyTerminated && (
                <div className={styles.wsDisconnectedBanner}>
                    Connection lost — reconnecting…
                </div>
            )}

            {lobbyTerminated && (
                <div className={styles.terminatedOverlay}>
                    <p className={styles.terminatedMessage}>Lobby terminated due to server error.</p>
                    <button className={styles.terminatedBtn} onClick={() => {
                        axios.post(`${process.env.REACT_APP_API_URL}/api/g/game/${gameID}/abandon/`, { reason: 'error' }, authHeader())
                            .catch((err) => console.error('Failed to abandon game:', err));
                        navigate('/main');
                    }}>Navigate back to Home</button>
                </div>
            )}

            {gameAbandoned && (
                <div className={styles.abandonedOverlay}>
                    <p className={styles.abandonedMessage}>
                        {abandonReason === 'error'
                            ? 'The game ended due to a server error.'
                            : abandonedBy
                                ? `This game was cancelled by ${abandonedBy}. Your stats won't be affected.`
                                : "This game was cancelled. Your stats won't be affected."}
                    </p>
                    <button className={styles.abandonedBtn} onClick={() => { closeSocket(); navigate('/main'); }}>
                        Return Home
                    </button>
                </div>
            )}

            {/* Scanline overlay */}
            <div className={styles.scanlines} />

            {/* Flash overlay for hit feedback */}
            {flashActive && <div className={styles.flashOverlay} />}

            {/* Sink message */}
            {sinkMessage && <div className={styles.sinkText}>{sinkMessage}</div>}

            {/* Away score widget — top of screen */}
            <div className={`${styles.scoreWidget} ${styles.scoreWidgetAway}`}>
                <span className={styles.widgetTeamLabel}>AWAY</span>
                <span className={styles.teamNames}>{team2Player1} & {team2Player2}</span>
                <span className={styles.scoreDigit}>{score2}</span>
            </div>

            {/* Table area */}
            <div className={styles.tableArea}>
                <div className={styles.tableBoard}>
                    {/* Away cups — BK at top (far), FR at bottom (nearest center) */}
                    <div className={styles.tableSide}>
                        <div className={styles.cupRow}>{renderCup('Away', 'BK')}</div>
                        <div className={styles.cupRow}>{renderCup('Away', 'CT')}</div>
                        <div className={styles.cupRow}>
                            {renderCup('Away', 'RT')}
                            {renderCup('Away', 'LF')}
                        </div>
                        <div className={styles.cupRow}>{renderCup('Away', 'FR')}</div>
                    </div>

                    {/* Center divider line */}
                    <div className={styles.tableDivider} />

                    {/* Home cups — FR at top (nearest center), BK at bottom (far) */}
                    <div className={styles.tableSide}>
                        <div className={styles.cupRow}>{renderCup('Home', 'FR')}</div>
                        <div className={styles.cupRow}>
                            {renderCup('Home', 'LF')}
                            {renderCup('Home', 'RT')}
                        </div>
                        <div className={styles.cupRow}>{renderCup('Home', 'CT')}</div>
                        <div className={styles.cupRow}>{renderCup('Home', 'BK')}</div>
                    </div>
                </div>
            </div>

            {/* Home score widget — bottom of screen */}
            <div className={`${styles.scoreWidget} ${styles.scoreWidgetHome}`}>
                <span className={styles.scoreDigit}>{score1}</span>
                <span className={styles.teamNames}>{team1Player1} & {team1Player2}</span>
                <span className={styles.widgetTeamLabel}>HOME</span>
            </div>

            {/* Saves rail — right edge */}
            <div className={styles.savesRail}>
                <div className={styles.savesRailLabel}>SAVES</div>
                {[
                    { player: team1Player1, team: 'home' },
                    { player: team1Player2, team: 'home' },
                    { player: team2Player1, team: 'away' },
                    { player: team2Player2, team: 'away' },
                ].map(({ player, team }) => (
                    <div key={player} className={`${styles.saveRow} ${team === 'home' ? styles.saveRowHome : styles.saveRowAway}`}>
                        <span className={styles.savePlayerName}>{player}</span>
                        <span className={styles.saveCount}>{saves[player] ?? 0}</span>
                        <div className={styles.saveControls}>
                            <button onClick={() => handleSaveAdjust(player, 1)}>+</button>
                            <button onClick={() => handleSaveAdjust(player, -1)}>-</button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Quit game button */}
            <button
                className={styles.quitBtn}
                onClick={() => { closePanel(); setUndoStep(0); setQuitStep(1); }}
            >
                QUIT GAME
            </button>

            {/* Error message */}
            {errorMessage && <div className={styles.errorBanner}>{errorMessage}</div>}

            {/* UNDO button — visible after any successful hit */}
            {lastAction && undoStep === 0 && (
                <button
                    className={styles.undoBtn}
                    onClick={() => { closePanel(); setUndoStep(1); }}
                >
                    ↩ UNDO
                </button>
            )}

            {/* Undo confirmation panel */}
            {undoStep > 0 && lastAction && (
                <div className={styles.slidePanel}>
                    <div className={styles.slidePanelInner}>
                        {undoStep === 1 && (
                            <div className={styles.confirmScreen}>
                                <div className={styles.undoLabel}>
                                    Undoing {lastAction.action_type} by {lastAction.player}
                                </div>
                                <div className={styles.confirmButtons}>
                                    <button className={styles.confirmBtnAmber} onClick={() => setUndoStep(2)}>
                                        UNDO
                                    </button>
                                    <button className={styles.confirmBtnDim} onClick={() => setUndoStep(0)}>
                                        CANCEL
                                    </button>
                                </div>
                            </div>
                        )}
                        {undoStep === 2 && (
                            <div className={styles.confirmScreen}>
                                <div className={styles.undoLabel}>
                                    Undoing {lastAction.action_type} by {lastAction.player}
                                </div>
                                <div className={styles.confirmButtons}>
                                    <button
                                        className={styles.confirmBtnRed}
                                        onClick={() => revertDB()}
                                    >
                                        CONFIRM UNDO
                                    </button>
                                    <button className={styles.confirmBtnDim} onClick={() => setUndoStep(1)}>
                                        GO BACK
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Quit confirmation panel */}
            {quitStep > 0 && (
                <div className={styles.slidePanel}>
                    <div className={styles.slidePanelInner}>
                        {quitStep === 1 && (
                            <div className={styles.confirmScreen}>
                                <div className={styles.undoLabel}>
                                    End this game early? No stats will be saved.
                                </div>
                                <div className={styles.confirmButtons}>
                                    <button className={styles.confirmBtnRed} onClick={() => setQuitStep(2)}>
                                        QUIT EARLY
                                    </button>
                                    <button className={styles.confirmBtnDim} onClick={() => setQuitStep(0)}>
                                        CANCEL
                                    </button>
                                </div>
                            </div>
                        )}
                        {quitStep === 2 && (
                            <div className={styles.confirmScreen}>
                                <div className={styles.undoLabel}>
                                    This will end the game for everyone. Are you sure?
                                </div>
                                <div className={styles.confirmButtons}>
                                    <button
                                        className={styles.confirmBtnRed}
                                        onClick={async () => {
                                            setQuitStep(0);
                                            try {
                                                await axios.post(
                                                    `${process.env.REACT_APP_API_URL}/api/g/game/${gameID}/abandon/`,
                                                    {},
                                                    authHeader()
                                                );
                                            } catch (err) {
                                                console.error('Failed to quit game:', err);
                                                showError('Failed to quit game. Please try again.');
                                            }
                                        }}
                                    >
                                        CONFIRM QUIT
                                    </button>
                                    <button className={styles.confirmBtnDim} onClick={() => setQuitStep(1)}>
                                        GO BACK
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Bottom slide-up panel — player + size selection */}
            {cupPosition && !undoStep && (() => {
                const isHomeCup = cupPosition.team === 'Home';
                const likelyShooters = isHomeCup
                    ? [{ name: team2Player1, team: 'away' }, { name: team2Player2, team: 'away' }]
                    : [{ name: team1Player1, team: 'home' }, { name: team1Player2, team: 'home' }];
                const ownTeam = isHomeCup
                    ? [{ name: team1Player1, team: 'home' }, { name: team1Player2, team: 'home' }]
                    : [{ name: team2Player1, team: 'away' }, { name: team2Player2, team: 'away' }];
                const isOwnCup = panelPlayer ? boolCheckOwnCup(panelPlayer, cupPosition) : false;
                const cupIsHalf = currentCupValues[cupPosition.team]?.[cupPosition.position] === 0.5;
                const fullDisabled = isOwnCup || cupIsHalf;
                const effectiveSize = fullDisabled ? 'half' : panelSize;
                const confirmReady = !!panelPlayer;

                const handlePlayerClick = (playerName) => {
                    setPanelPlayer(playerName);
                    const willBeOwnCup = boolCheckOwnCup(playerName, cupPosition);
                    if (willBeOwnCup) setPanelSize('half');
                };

                return (
                    <div className={styles.slidePanel}>
                        <div className={styles.slidePanelInner}>
                            <div className={styles.panelHeader}>
                                <h3 className={styles.panelTitle}>
                                    {cupPosition.team} {positionNames[cupPosition.position]} cup hit
                                </h3>
                                <button className={styles.panelCancel} onClick={closePanel}>Cancel</button>
                            </div>
                            <div className={styles.panelColumns}>
                                <div className={styles.panelCol}>
                                    <div className={styles.panelColLabel}>WHO SHOT?</div>
                                    {likelyShooters.map(({ name, team }) => (
                                        <button
                                            key={name}
                                            className={`${styles.panelBtn} ${team === 'home' ? styles.panelBtnHome : styles.panelBtnAway} ${panelPlayer === name ? styles.panelBtnSelected : ''}`}
                                            onClick={() => handlePlayerClick(name)}
                                        >
                                            {name}
                                        </button>
                                    ))}
                                    <div className={styles.panelOwnDivider} />
                                    {ownTeam.map(({ name, team }) => (
                                        <button
                                            key={name}
                                            className={`${styles.panelBtn} ${team === 'home' ? styles.panelBtnHome : styles.panelBtnAway} ${styles.panelBtnDim} ${panelPlayer === name ? styles.panelBtnSelected : ''}`}
                                            onClick={() => handlePlayerClick(name)}
                                        >
                                            {name}
                                        </button>
                                    ))}
                                </div>

                                <div className={styles.panelColDivider} />

                                <div className={styles.panelCol}>
                                    <div className={styles.panelColLabel}>HOW MUCH?</div>
                                    <button
                                        className={`${styles.sizeToggle} ${effectiveSize === 'full' ? styles.sizeToggleActive : ''} ${fullDisabled ? styles.sizeToggleDisabled : ''}`}
                                        onClick={() => !fullDisabled && setPanelSize('full')}
                                        disabled={fullDisabled}
                                    >
                                        FULL
                                    </button>
                                    <button
                                        className={`${styles.sizeToggle} ${effectiveSize === 'half' ? styles.sizeToggleActiveHalf : ''}`}
                                        onClick={() => setPanelSize('half')}
                                    >
                                        HALF
                                    </button>
                                    {isOwnCup && <p className={styles.panelNote}>Own cup — half only</p>}
                                    {!isOwnCup && cupIsHalf && <p className={styles.panelNote}>Already half — half only</p>}
                                </div>
                            </div>

                            <button
                                className={`${styles.confirmBtn} ${confirmReady ? styles.confirmBtnReady : ''}`}
                                onClick={handleConfirm}
                                disabled={!confirmReady}
                            >
                                CONFIRM HIT
                            </button>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default Scoreboard;
